import classNames from 'classnames';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React from 'react';
import {
  SendOutlined,
  TeamOutlined,
  SyncOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Button, Switch } from '@mm/components';
import { StoreState } from '@mm/state/store-interfaces';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { useDispatch, useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import modalActions from '@mm/state/modules/modalsState/actions';
import { ReactComponent as CopyIcon } from 'assets/icons/duplicate-icon.svg';
import css from './AutomationRow.module.css';
import unicornSrc from './mdi_unicorn.svg';
import { useAutomationStatus } from '../../../../Automations/use-automation-status';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import {
  AutomationState,
  useAutomation,
} from '../../../../Automations/AutomationEdit/use-automation';
import { RowComponent } from '../../CampaignsTable';
import { ReviewBadge } from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/ReviewBadge/ReviewBadge';
import { Menu } from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { useResolutionInfo } from '@mm/hooks';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';

export const AutomationRow: RowComponent = ({
  item,
  fetchCurrentActiveKind,
}) => {
  const { id, name } = item;
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= 700;

  const { history } = useRouter();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { pagination } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};

  const stats = useSelector((state: StoreState) => state.automation.stats);
  const automationStats = stats[id];

  const automation = useAutomation({
    id: item.id,
    initialState: {
      source: item,
      automation: item,
      busy: '',
      error: '',
    } as AutomationState,
  });

  const automationStatus = useAutomationStatus();
  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const status = automationStatus.get(item);

  const dispatch = useDispatch();
  const onCloneAutomationClick = () => {
    dispatch(
      modalActions.openCloneAutomationModal({
        campaignId: automation.state.source?.id,
        retrieveCampaigns: true,
      }),
    );
  };
  const isAutomaticallyCreatedCampaign = Boolean(
    automation.state.source?.system,
  );

  const fetchCampaigns = async () => {
    await campaignsOverviewCRUDActions.retrieveAll({
      botId: chatbotId,
      kind: 'automation',
      pagination: {
        page: pagination?.page || 1,
        perPage: pagination?.perPage || 8,
      },
    });
  };

  const onDelete = () => {
    automation.actions.deleteAutomation({
      skipPush: true,
      onSuccess: fetchCampaigns,
    });
  };

  const menu = [
    {
      disabled: false,
      children: (
        <div className={css.menuItem}>
          {' '}
          <CopyOutlined className={css.menuItemIcon} /> Clone
        </div>
      ),
      onClick: onCloneAutomationClick,
    },
    {
      disabled: isAutomaticallyCreatedCampaign,
      children: (
        <div className={css.menuItem}>
          {' '}
          <DeleteOutlined className={css.menuItemIcon} /> Delete
        </div>
      ),
      onClick: onDelete,
    },
  ];

  const navigateToEditRoute = () => {
    history.push(
      BotBuilderRoutes.automationEdit.getLink({
        chatbotId,
        id,
      }),
    );
  };

  const onChange = async () => {
    if (!status.active) {
      await automation.actions.start();
    }

    if (status.active && !status.paused) {
      await automation.actions.pause();
    }

    if (status.active && status.paused) {
      await automation.actions.start();
    }

    fetchCurrentActiveKind?.(pagination?.page);
  };

  const statusFormatted = automationStatus.format(item);
  const inactive = statusFormatted !== 'Active';

  return (
    <div
      className={classNames(css.container, {
        [css.inactive]: inactive,
      })}
    >
      {item.needsReview && (
        <ReviewBadge
          onClick={navigateToEditRoute}
          className={css.reviewBadge}
          withIcon
        />
      )}

      <div className={css.info}>
        <span className={css.title} onClick={navigateToEditRoute}>
          <abbr title={name}>{name}</abbr>
        </span>
        {!isMobile && (
          <Button css={{ container: css.button }} onClick={navigateToEditRoute}>
            Go to automation
          </Button>
        )}
      </div>

      <div className={css.stats}>
        <div className={css.column}>
          <div className={css.icon}>
            <SendOutlined />
          </div>
          <div className={css.number}>
            {automationStats?.messagesSentCount || 0}
          </div>
          <div className={css.placeholder}>Messages Sent</div>
        </div>
        <div className={css.column}>
          <div className={css.icon}>
            <TeamOutlined />
          </div>
          <div className={css.number}>
            {automationStats?.contactsCount || 0}
          </div>
          <div className={css.placeholder}>Started</div>
        </div>
        <div className={css.column}>
          <div className={css.icon}>
            <SyncOutlined />
          </div>
          <div className={css.number}>-</div>
          <div className={css.placeholder}>Phone/Email Captured</div>
        </div>
        <div className={classNames(css.column, css.unicorn)}>
          <div className={css.icon}>
            <img src={unicornSrc} alt="Unicorn" />
          </div>
          <div className={css.number}>-</div>
          <div className={css.placeholder}>Unicorns</div>
        </div>
      </div>

      <div className={css.switch}>
        <Switch
          css={{
            container: classNames(css.switchInput, {
              [css.swithcInputDisabled]: inactive,
            }),
            label: css.label,
            thumb: css.thumbDisabled,
          }}
          checked={!inactive}
          onChange={onChange}
          label={statusFormatted}
        />
        {isMobile && (
          <Button
            css={{ container: isMobile ? css.mobileButton : css.button }}
            onClick={navigateToEditRoute}
          >
            Go to automation
          </Button>
        )}
      </div>
      {menu?.length && (
        <Menu
          className={css.menu}
          menu={menu}
          direction="bottom"
          menuButton={
            <Button css={{ container: css.menuButton }} color="secondary">
              <MoreOutlined className={css.moreIcon} />
            </Button>
          }
          css={{ menu: css.nodeMenu }}
        />
      )}
    </div>
  );
};
